/**
 * Settings Service
 * 
 * Handles all settings-related API calls
 * 
 * API Endpoints:
 * 
 * Complaint Types (/cvo/complaint):
 * - GET /cvo/complaint - Fetch complaint types
 * - POST /cvo/complaint - Add complaint type (payload: { name: string })
 * - PUT /cvo/complaint/:id - Update complaint type (payload: { name: string })
 * - DELETE /cvo/complaint/:id - Delete complaint type
 * 
 * Complaint Statuses (/cvo/complaint-status):
 * - GET /cvo/complaint-status - Fetch complaint statuses
 * - POST /cvo/complaint-status - Add complaint status (payload: { name: string })
 * - PUT /cvo/complaint-status/:id - Update complaint status (payload: { name: string })
 * - DELETE /cvo/complaint-status/:id - Delete complaint status
 * 
 * Priorities (/cvo/priorities):
 * - GET /cvo/priorities - Fetch priorities
 * - POST /cvo/priorities - Add priority (payload: { name: string })
 * - PUT /cvo/priorities/:id - Update priority (payload: { name: string })
 * - DELETE /cvo/priorities/:id - Delete priority
 * 
 * Officer Designations (/cvo/officer-designations):
 * - GET /cvo/officer-designations - Fetch officer designations
 * - POST /cvo/officer-designations - Add officer designation (payload: { name: string })
 * - PUT /cvo/officer-designations/:id - Update officer designation (payload: { name: string })
 * - DELETE /cvo/officer-designations/:id - Delete officer designation
 * 
 * Departments (/cvo/departments):
 * - GET /cvo/departments - Fetch departments
 * - POST /cvo/departments - Add department (payload: { name: string })
 * - PUT /cvo/departments/:id - Update department (payload: { name: string })
 * - DELETE /cvo/departments/:id - Delete department
 * 
 * Authority Privileges (/cvo/authority-privileges):
 * - GET /cvo/authority-privileges - Fetch authority privileges
 * - POST /cvo/authority-privileges - Add authority privilege (payload: { name: string })
 * - PUT /cvo/authority-privileges/:id - Update authority privilege (payload: { name: string })
 * - DELETE /cvo/authority-privileges/:id - Delete authority privilege
 * 
 * CVO Privileges (/cvo/cvo-privileges):
 * - GET /cvo/cvo-privileges - Fetch CVO privileges
 * - POST /cvo/cvo-privileges - Add CVO privilege (payload: { name: string })
 * - PUT /cvo/cvo-privileges/:id - Update CVO privilege (payload: { name: string })
 * - DELETE /cvo/cvo-privileges/:id - Delete CVO privilege
 */

import apiClient, { API_ENDPOINTS } from './api';
import { handleApiError } from '../utils/apiErrorHandler';

// Map Redux state keys to API endpoint keys
const typeToEndpointMap = {
  complaintTypes: 'complaintTypes',
  complaintStatuses: 'complaintStatuses',
  complaintPriorities: 'priorities',
  departments: 'departments',
  officerDesignations: 'designations',
  authorityPrivileges: 'authorityPrivileges',
  cvoPrivileges: 'cvoPrivileges',
};

const settingsService = {
  /**
   * Get all settings
   * @returns {Promise<Object>} All settings
   */
  async getSettings() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.settings.get);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch settings' });
    }
  },

  /**
   * Update settings
   * @param {Object} settingsData - Settings data to update
   * @returns {Promise<Object>} Updated settings
   */
  async updateSettings(settingsData) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.settings.update, settingsData);
      return {
        success: true,
        data: response.data,
        message: 'Settings updated successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to update settings' });
    }
  },

  /**
   * Get complaint types
   * @returns {Promise<Object>} List of complaint types
   */
  async getComplaintTypes() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.settings.complaintTypes);
      return {
        success: true,
        data: response.data.data || response.data, // Handle { message, data: [...] } format
        message: response.data.message,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch complaint types' });
    }
  },

  /**
   * Get complaint statuses
   * @returns {Promise<Object>} List of statuses
   */
  async getComplaintStatuses() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.settings.complaintStatuses);
      return {
        success: true,
        data: response.data.data || response.data, // Handle { message, data: [...] } format
        message: response.data.message,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch complaint statuses' });
    }
  },

  /**
   * Get priorities
   * @returns {Promise<Object>} List of priorities
   */
  async getPriorities() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.settings.priorities);
      return {
        success: true,
        data: response.data.data || response.data, // Handle { message, data: [...] } format
        message: response.data.message,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch priorities' });
    }
  },

  /**
   * Get departments
   * @returns {Promise<Object>} List of departments
   */
  async getDepartments() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.settings.departments);
      return {
        success: true,
        data: response.data.data || response.data, // Handle { message, data: [...] } format
        message: response.data.message,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch departments' });
    }
  },

  /**
   * Get designations
   * @returns {Promise<Object>} List of designations
   */
  async getDesignations() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.settings.designations);
      return {
        success: true,
        data: response.data.data || response.data, // Handle { message, data: [...] } format
        message: response.data.message,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch designations' });
    }
  },

  /**
   * Get authority privileges
   * @returns {Promise<Object>} List of authority privileges
   */
  async getAuthorityPrivileges() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.settings.authorityPrivileges);
      return {
        success: true,
        data: response.data.data || response.data, // Handle { message, data: [...] } format
        message: response.data.message,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch authority privileges' });
    }
  },

  /**
   * Get CVO privileges
   * @returns {Promise<Object>} List of CVO privileges
   */
  async getCVOPrivileges() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.settings.cvoPrivileges);
      return {
        success: true,
        data: response.data.data || response.data, // Handle { message, data: [...] } format
        message: response.data.message,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch CVO privileges' });
    }
  },

  /**
   * Add new setting option
   * @param {string} type - Setting type (complaintTypes, complaintStatuses, etc.)
   * @param {Object} data - New option data (format: { name: string })
   * @returns {Promise<Object>}
   */
  async addSettingOption(type, data) {
    try {
      // Map the type to the correct endpoint key
      const endpointKey = typeToEndpointMap[type] || type;
      const endpoint = API_ENDPOINTS.settings[endpointKey];
      
      // Ensure payload has correct format { name: string }
      const payload = { name: data.name || data };
      
      const response = await apiClient.post(endpoint, payload);
      return {
        success: true,
        data: response.data.data || response.data, // Handle { message, data: {...} } format
        message: response.data.message || 'Setting option added successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to add setting option' });
    }
  },

  /**
   * Update setting option
   * @param {string} type - Setting type
   * @param {string} id - Option ID
   * @param {Object} data - Data to update (format: { name: string })
   * @returns {Promise<Object>}
   */
  async updateSettingOption(type, id, data) {
    try {
      // Map the type to the correct endpoint key
      const endpointKey = typeToEndpointMap[type] || type;
      const endpoint = `${API_ENDPOINTS.settings[endpointKey]}/${id}`;
      
      // Ensure payload has correct format { name: string }
      const payload = { name: data.name || data };
      
      const response = await apiClient.put(endpoint, payload);
      return {
        success: true,
        data: response.data.data || response.data, // Handle { message, data: {...} } format
        message: response.data.message || 'Setting option updated successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to update setting option' });
    }
  },

  /**
   * Delete setting option
   * @param {string} type - Setting type
   * @param {string} id - Option ID
   * @returns {Promise<Object>}
   */
  async deleteSettingOption(type, id) {
    try {
      // Map the type to the correct endpoint key
      const endpointKey = typeToEndpointMap[type] || type;
      const endpoint = `${API_ENDPOINTS.settings[endpointKey]}/${id}`;
      
      await apiClient.delete(endpoint);
      return {
        success: true,
        message: 'Setting option deleted successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to delete setting option' });
    }
  },

  /**
   * Get authority dropdowns (complaint_types, complaint_statuses, departments, designations, priorities, authority_privileges)
   * @returns {Promise<Object>} All dropdown data
   */
  async getAuthorityDropdowns() {
    try {
      console.log('getAuthorityDropdowns: Calling API endpoint:', API_ENDPOINTS.authority.dropdowns);
      const response = await apiClient.get(API_ENDPOINTS.authority.dropdowns);
      console.log('getAuthorityDropdowns: API response:', response);
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message || 'Dropdown data fetched successfully',
      };
    } catch (error) {
      console.error('getAuthorityDropdowns: API error:', error);
      return handleApiError(error, { defaultMessage: 'Failed to fetch dropdown data' });
    }
  },
};

export default settingsService;

